<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { PhoneNumberInput } from '@/components/common';
import {
  PreferredDayOption,
  TEXT_MAX_LENGTH,
  getPreferredDayOptions,
  preferredTimeOptions,
} from '@/components/common/ContactModal/util';
import { isPlatformHg, isPlatformIs24 } from '@/helpers/Platform/Platform';
import { ContactRequest, ContactRequestPurpose } from '@/store/modules/localLanding/types';
import { getStepInputsLLPContactModal, trackingGenericClickEvent } from '@/tracking/tracking';
import {
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
  stepInputKeyMapContactModal,
} from '@/tracking/types';
import { ContactModalFrom, ContactModalType, PreferredTimeValue } from '@/types';
import {
  HgButton,
  HgInput,
  HgModal,
  HgRadioButton,
  HgSupportedLanguage,
} from '@smg-real-estate/domus';

// Types
interface PhoneData {
  valid: boolean;
  number: null | string;
}

interface ContactFormData {
  phoneData: PhoneData;
  firstName: string | null;
  lastName: string | null;
  preferredDay: string | null;
  preferredTime: PreferredTimeValue | null;
}

interface Props {
  from: ContactModalFrom;
  isVisible: boolean;
  modalType: ContactModalType;
  title: string;
  subTitle: string;
  trackingLabel?: string;
}

// Props and emits
const props = withDefaults(defineProps<Props>(), {
  trackingLabel: undefined,
});

const emit = defineEmits<{
  (event: 'onSubmit', contactRequestData: ContactRequest): void;
  (event: 'onClose'): void;
}>();

// Composables
const { t, locale } = useI18n();

// Constants
const defaultFormData: ContactFormData = {
  phoneData: {
    valid: true,
    number: null,
  },
  firstName: null,
  lastName: null,
  preferredDay: null,
  preferredTime: null,
};

// Reactive state
const isFormSubmitting = ref(false);
const platform = {
  isPlatformIs24: isPlatformIs24(),
  isPlatformHg: isPlatformHg(),
};

const previousFormData = reactive({
  phoneNumber: null as string | null,
  firstName: null as string | null,
  lastName: null as string | null,
  preferredDay: null as string | null,
  preferredTime: null as PreferredTimeValue | null,
});

// Computed properties
const trackingLabelValue = computed(() => {
  return props.trackingLabel || props.from;
});

const formattedPreferredDayOptions = computed(() => {
  return getPreferredDayOptions().map((item: PreferredDayOption) => ({
    text: t(item.text),
    date: item.date,
    value: item.value,
  }));
});

const phoneData = reactive<PhoneData>({
  valid: true,
  number: null,
});

const phoneNumber = computed({
  get(): string | null {
    return phoneData.number;
  },
  set(value: any): void {
    if (value) {
      Object.assign(phoneData, value);
    }
  },
});

const isModalVisible = computed({
  get(): boolean {
    return props.isVisible;
  },
  set(value: boolean) {
    return value;
  },
});

// Validation rules
const validationRules = computed(() => ({
  firstName: {
    required,
    maxLength: TEXT_MAX_LENGTH,
  },
  lastName: {
    required,
    maxLength: TEXT_MAX_LENGTH,
  },
  phoneNumber: {
    required,
  },
  phoneData: {
    formatValid: (phoneData: { valid: boolean | undefined }) => {
      return phoneData.valid;
    },
  },
  preferredDay: {
    required,
    $lazy: true,
  },
  preferredTime: {
    required,
    $lazy: true,
  },
}));

const formState = reactive({
  firstName: '',
  lastName: '',
  phoneNumber,
  phoneData,
  preferredDay: '',
  preferredTime: '',
});

// Vuelidate setup
const v$ = useVuelidate(validationRules, formState, { $autoDirty: true });

// Methods
const trackFormSteps = async (fieldName: keyof typeof previousFormData): Promise<void> => {
  // TODO: A bug here to always trigger the event twice on @change
  await nextTick();

  const currentValue = fieldName === 'phoneNumber' ? phoneNumber.value : v$.value[fieldName].$model;
  if (currentValue === previousFormData[fieldName]) {
    return;
  }

  previousFormData[fieldName] = currentValue as any;

  const stepInputs = getStepInputsLLPContactModal({
    firstName: v$.value.firstName.$model,
    lastName: v$.value.lastName.$model,
    phoneNumber: phoneNumber.value,
    preferredDay: v$.value.preferredDay.$model,
    preferredTime: v$.value.preferredTime.$model,
  } as any);

  trackingGenericClickEvent(
    {
      event: LOCAL_LANDING_PAGE_EVENT.FORM_STEPS,
      category: LOCAL_LANDING_PAGE_CATEGORY.FORM_STEPS,
      action: LOCAL_LANDING_PAGE_ACTION.INPUT_CALLBACK,
      label: stepInputKeyMapContactModal[fieldName],
    },
    { h_funnel_step_input_141: stepInputs },
  );
};

const closeContactModal = (): void => {
  if (isFormSubmitting.value) {
    return;
  }
  emit('onClose');
};

const resetFormData = (): void => {
  Object.assign(phoneData, defaultFormData.phoneData);
  Object.assign(formState, {
    firstName: defaultFormData.firstName ?? '',
    lastName: defaultFormData.lastName ?? '',
    preferredDay: defaultFormData.preferredDay ?? '',
    preferredTime: defaultFormData.preferredTime ?? '',
  });
  v$.value.$reset();
  isFormSubmitting.value = false;
};

const handleSubmitFormData = async (): Promise<void> => {
  trackingGenericClickEvent({
    event: LOCAL_LANDING_PAGE_EVENT.FORM_STEPS,
    category: LOCAL_LANDING_PAGE_CATEGORY.FORM_STEPS,
    action: LOCAL_LANDING_PAGE_ACTION.SUBMIT_CALLBACK,
    label: trackingLabelValue.value,
  });

  const isValid = await v$.value.$validate();
  if (!isValid || isFormSubmitting.value) {
    return;
  }

  isFormSubmitting.value = true;

  const contactRequestData = {
    phoneNumber: v$.value.phoneNumber.$model as string,
    firstName: v$.value.firstName.$model as string,
    lastName: v$.value.lastName.$model as string,
    preferredDay: v$.value.preferredDay.$model as string,
    preferredTime: v$.value.preferredTime.$model! as any,
    purpose:
      props.modalType === ContactModalType.AGENCY_RECOMMENDATION
        ? ContactRequestPurpose.AGENCY_RECOMMENDATION
        : ContactRequestPurpose.CALL_ME_BACK,
    language: locale.value as HgSupportedLanguage,
  };

  emit('onSubmit', contactRequestData);
};

// Watchers
watch(
  () => props.isVisible,
  (value) => {
    if (value) {
      trackingGenericClickEvent({
        event: LOCAL_LANDING_PAGE_EVENT.FEATURE_DISPLAY,
        category: LOCAL_LANDING_PAGE_CATEGORY.FEATURE_DISPLAY,
        action: LOCAL_LANDING_PAGE_ACTION.DISPLAY_CALLBACK,
        label: trackingLabelValue.value,
      });
    } else {
      resetFormData();
    }
  },
);
</script>

<template>
  <HgModal
    v-model="isModalVisible"
    :containerCssClass="$style.container"
    @close="closeContactModal"
  >
    <template #title>
      <h3 :class="$style.title">{{ title }}</h3>
    </template>
    <section :class="$style.agentsWrapper">
      <div :class="$style.agentsAvatar">
        <img
          src="../../../assets/images/local-landing-agents-avatar/agent-avatar1.webp"
          alt="agent-avatar1"
        />
        <img
          src="../../../assets/images/local-landing-agents-avatar/agent-avatar2.webp"
          alt="agent-avatar2"
        />
        <img
          src="../../../assets/images/local-landing-agents-avatar/agent-avatar3.webp"
          alt="agent-avatar3"
        />
      </div>
      <div :class="$style.descriptions">
        <span>{{ platform.isPlatformIs24 ? 'ImmoScout24' : 'Homegate' }}</span>
        <span>{{ $t('localLanding.contactForm.descriptions') }}</span>
      </div>
    </section>
    <section :class="$style.subTitle">
      <p v-html="subTitle" />
    </section>
    <section :class="$style.contactForm">
      <div :class="$style.username">
        <HgInput
          v-model="v$.firstName.$model as any"
          :label="$t('localLanding.contactForm.firstName.label')"
          name="firstname"
          autocomplete="on"
          :updateModelOnInput="true"
          :isDirty="v$.firstName.$dirty"
          :isInvalid="v$.firstName.$invalid"
          @blur="trackFormSteps('firstName')"
        >
          <template #errorMessages>
            <span v-if="v$.firstName.required.$invalid">{{
              $t('localLanding.contactForm.validations.firstName.required')
            }}</span>
            <span v-if="v$.firstName.maxLength.$invalid">{{
              $t('localLanding.contactForm.validations.firstName.maxLength')
            }}</span>
          </template>
        </HgInput>
        <HgInput
          v-model="v$.lastName.$model as any"
          :label="$t('localLanding.contactForm.lastName.label')"
          name="lastName"
          autocomplete="on"
          :updateModelOnInput="true"
          :isDirty="v$.lastName.$dirty"
          :isInvalid="v$.lastName.$invalid"
          @blur="trackFormSteps('lastName')"
        >
          <template #errorMessages>
            <span v-if="v$.lastName.required.$invalid">{{
              $t('localLanding.contactForm.validations.lastName.required')
            }}</span>
            <span v-if="v$.lastName.maxLength.$invalid">{{
              $t('localLanding.contactForm.validations.lastName.maxLength')
            }}</span>
          </template>
        </HgInput>
      </div>
      <div :class="$style.phoneNumber">
        <PhoneNumberInput
          v-model="v$.phoneNumber.$model as any"
          name="phoneNumber"
          :label="$t('localLanding.contactForm.phoneNumber.label')"
          inputmode="numeric"
          :isDirty="v$.phoneNumber.$dirty || v$.phoneData.$dirty"
          :isInvalid="v$.phoneNumber.$invalid || v$.phoneData.$invalid"
          :isRequired="v$.phoneNumber.required.$invalid"
          @blur="trackFormSteps('phoneNumber')"
        >
          <template v-if="v$.phoneNumber.$invalid || v$.phoneData.$invalid" #errorMessages>
            <span v-if="v$.phoneNumber.$dirty && v$.phoneNumber.required.$invalid">
              {{
                $t('errorMessage.requiredPhone', {
                  fieldName: $t('numberVerification.numberLabel'),
                })
              }}
            </span>
            <span v-else-if="v$.phoneData.formatValid.$invalid">
              {{
                $t('errorMessage.invalidPhone', {
                  fieldName: $t('numberVerification.numberLabel'),
                })
              }}
            </span>
          </template>
        </PhoneNumberInput>
      </div>
      <div :class="$style.preferredTimePeriod">
        <div :class="$style.selectorWrapper">
          <div :class="$style.preferredTimePeriodText">
            {{ $t('localLanding.contactForm.favoriteDay.label') }}
          </div>
          <HgRadioButton
            v-for="item in formattedPreferredDayOptions"
            :key="item.value"
            v-model="v$.preferredDay.$model as any"
            :class="$style.radioText"
            :inputValue="item.value"
            :label="item.text"
            @change="trackFormSteps('preferredDay')"
          >
            {{ item.text }},
            <span :class="$style.caption">{{ item.date }}</span>
          </HgRadioButton>
          <span
            v-if="v$.preferredDay.required.$invalid"
            :class="[
              $style.errorMessage,
              { [$style.is24]: platform.isPlatformIs24, [$style.hg]: platform.isPlatformHg },
            ]"
          >
            {{ $t('localLanding.contactForm.validations.favoriteDay.required') }}
          </span>
        </div>
        <div :class="$style.selectorWrapper">
          <div :class="$style.preferredTimePeriodText">
            {{ $t('localLanding.contactForm.preferredTime.label') }}
          </div>
          <HgRadioButton
            v-for="item in preferredTimeOptions"
            :key="item.value"
            v-model="v$.preferredTime.$model as any"
            :class="$style.radioText"
            :inputValue="item.value"
            :label="item.text"
            @change="trackFormSteps('preferredTime')"
          />
          <span
            v-if="v$.preferredTime.required.$invalid"
            :class="[
              $style.errorMessage,
              { [$style.is24]: platform.isPlatformIs24, [$style.hg]: platform.isPlatformHg },
            ]"
          >
            {{ $t('localLanding.contactForm.validations.preferredTime.required') }}
          </span>
        </div>
      </div>
    </section>
    <section :class="$style.disclaimer">
      <span v-html="$t('contactData.privacyMessage')"></span>
    </section>
    <template #footer>
      <HgButton theme="secondary-bordered" @click="closeContactModal">{{
        $t('localLanding.contactForm.cancelButton.label')
      }}</HgButton>
      <HgButton theme="primary" :disabled="isFormSubmitting" @click="handleSubmitFormData">{{
        $t('localLanding.contactForm.submitButton.label')
      }}</HgButton>
    </template>
  </HgModal>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.container {
  flex: unset;
  margin-right: $space-150;
  margin-left: $space-150;

  @include grid-media-query(sm) {
    margin-right: 0;
    margin-left: 0;
  }

  h3 {
    @include vertical-margin(0, 0);
    @include heading-3-semi-bold;
  }

  .title {
    font-weight: 800;
  }

  div:nth-child(2) {
    overflow-y: unset !important;
  }

  .agentsWrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: $space-50;
    background-color: $color-grey-30;
    border-radius: $border-radius-l;

    .agentsAvatar {
      display: flex;

      img {
        width: 60px;
        height: 60px;
        object-fit: cover;
        border-radius: 50%;
      }

      :not(:first-child) {
        margin-left: -$space-200;

        @include grid-media-query(sm) {
          margin-left: -$space-100;
        }
      }
    }

    .descriptions {
      margin-left: $space-75;

      @include body-3-bold;

      span:first-child {
        display: block;
        font-weight: 800;
      }

      span:last-child {
        display: block;
        font-weight: 400;
      }
    }
  }

  .subTitle {
    display: block;

    @include vertical-margin($space-100, $space-100);
    @include body-2;
  }

  .contactForm {
    width: 100%;
    padding-bottom: $space-100;
    margin-top: $space-100;
    border-bottom: 1px solid $color-grey-60;

    .username {
      display: flex;
      gap: $space-100;

      div {
        flex: 1;
      }
    }

    .phoneNumber {
      margin-top: $space-100;
    }

    .preferredTimePeriod {
      display: flex;
      flex-direction: column;
      gap: $space-100;
      margin-top: $space-100;

      @include grid-media-query(sm) {
        flex-direction: row;
      }

      .selectorWrapper {
        display: flex;
        flex-direction: column;
        gap: $space-25;
        min-width: calc(50% - $space-50);

        .errorMessage {
          display: block;
          margin-top: $space-25;
          font-size: 0.875rem;

          &.is24 {
            color: $color-red-100;
          }

          &.hg {
            color: $color-orange-80;
          }
        }
      }

      .selectPlaceholder {
        button {
          color: $color-grey-70;
        }
      }

      .preferredTimePeriodText {
        @include body-2-bold;

        & {
          padding-bottom: $space-50;
        }
      }

      .radioText {
        @include body-3-semi-bold;
      }

      .caption {
        @include body-2($color-grey-90);
      }
    }
  }

  .disclaimer {
    margin-top: $space-100;

    @include body-3;
  }
}
</style>
